import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";

import ContactDirectoryHeader from "@/app/pages/contactDirectory/ContactDirectoryHeader";
import ContactProfileHeader from "@/app/pages/contactProfile/ContactProfileHeader";
import Overview from "@/app/pages/contactProfile/Overview";
import ContactProfileColleagues from "@/app/pages/contactProfile/ContactProfileColleagues";
import SimilarContacts from "@/app/pages/contactProfile/SimilarContacts";
import ContactProfileMoversAndShakers from "@/app/pages/contactProfile/ContactProfileMoversAndShakers";
import HooversAdBlock from  "@/app/pages/companyProfile/hooversAdBlock";
import ScoreRatingsAdPurple from "@/app/pages/companyProfile/scoreRatingsAdPurple";
import ContactProfileFAQ from "@/app/pages/contactProfile/ContactProfileFAQ";
import ContactProfileRelatedIndustries from "@/app/pages/contactProfile/ContactProfileRelatedIndustries";
import BottomGrayBanner from "@/app/pages/contactProfile/BottomGrayBanner";
import Loader from "@/components/loader";

import { dnbComLog, isNotUndefined, LogSeverity } from "@/shared/FunctionCore";
import { getContactProfileData } from "@/services/contactDirectory";
import "@/app/pages/contactProfile/styles/contact-directory.scss";
import Overlay from "@/components/overlay";
import { usePageURL } from "@/shared/customHooks";
import { setDigitalData } from "@/services/setDigitalData";
import SkyscraperAdComponent from "@/components/ads/SkyscraperAdComponent";
import { contactDirectoryDesktopThree, contactDirectoryDesktopTwo, contactProfileGoogleAdsData } from "@/components/ads/GoogleAdsData";
import MarketPlaceAds from "@/components/ads/MarketPlaceAds";
import SingleAds from "@/components/ads/SingleAds";

const ContactProfile = (): JSX.Element => {
  const { pathname } = useLocation();
  const [contactProfileData, setContactProfileData] = useState(null);
  const navigate = useNavigate();
  const { countryCode } = usePageURL();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  setDigitalData({ gcpPageName: "Contact Profile" });

  const fetchData = () => {
    setIsLoading(true);

    try {
      let path = pathname.substr(pathname.indexOf("/") + 1);
      const splittedPath = path.split("/");

      if (isNotUndefined(splittedPath[3])) {
        const encodedQS = encodeURI(splittedPath[3]);
        splittedPath[3] = encodedQS;
        splittedPath.splice(0, 1);
        path = splittedPath.join("/");
      }

      getContactProfileData(path)
        .then((res) => {
          if (res && res.data !== null) {
            setContactProfileData(res.data);
          }
          setIsLoading(false);
        })
        .catch((error: AxiosError) => { 
          setIsLoading(false);
          if (error.response?.status === 403) {
            navigate("/contact-directory.html");
          } else {
            dnbComLog(LogSeverity.ERROR, error);
          }
        });
    } catch {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (pathname) {
      fetchData();
    }
    // else {
    // Display no data found page // Coming soon....
    // }
  }, [pathname]);

  const hooversSignupPath = t("contactProfile.hooversSignupPath");

  const {
    headerData,
    colleaguesData,
    relatedIndustries,
    contactProfileFaqResult,
  } = useMemo(() => {
    const contactProfilePageData = contactProfileData?.contactProfilePageData;

    return {
      headerData: {
        contactProfilePageData,
        contactProfileBreadcrumbFragmentComponentParameter: {
          ...contactProfileData?.contactProfileHeaderFragmentParameter
            .contactProfileBreadcrumbFragmentComponentParameter,
          hooversSignupPath,
        },
      },
      colleaguesData: {
        contactPeersData: contactProfileData?.contactPeersData,
        contactProfilePageData,
        hooversSignupPath,
      },
      relatedIndustries: contactProfileData?.relatedIndustries,
      contactProfileFaqResult:
        contactProfileData?.contactProfileFaqFragmentParameter
          ?.contactProfileFaqResult,
    };
  }, [contactProfileData]);

  if (isLoading) return <Loader inProgress={isLoading} />;

  return (
    <>
      <div className="ContactDirectoryHeader">
        <ContactDirectoryHeader />
      </div>
      <SkyscraperAdComponent googleAdsData={contactProfileGoogleAdsData} />
      {contactProfileData && (
        <>
          <div className="ContactProfileHeader">
            <ContactProfileHeader {...headerData} colleaguesData= {colleaguesData} />
          </div>
          <Overview
            contactProfilePageData={contactProfileData?.contactProfilePageData}
          />
          <HooversAdBlock forPage="contactProfile" />
          <ContactProfileColleagues {...colleaguesData} />
          <SimilarContacts
            contactSimilarContactsData={
              contactProfileData?.contactSimilarContactsData
            }
          />
          <MarketPlaceAds page="ContactProfile" />
          <ContactProfileMoversAndShakers
            contactMoversShakersData={
              contactProfileData?.contactMoversShakersData
            }
          />
          <div className="spacing-pl-10 spacing-pr-10 pt-4">
            <ScoreRatingsAdPurple forPage="contactProfilePurpleAd" />
          </div>
          <ContactProfileFAQ
            contactProfileFaqResult={contactProfileFaqResult}
          />
          <SingleAds AdName={contactDirectoryDesktopThree} />
         {Object.keys(relatedIndustries)?.length && <ContactProfileRelatedIndustries
            relatedIndustries={relatedIndustries}
          />}
        </>
      )}
      <BottomGrayBanner />
      <SingleAds AdName={contactDirectoryDesktopTwo} />
      <Overlay
        countryCode={countryCode}
        isEmailDisplay={false}
        isPhoneDisplay={false}
        isRecaptchaDisplay={["us"].includes(countryCode)}
        recaptchaActionName='contact_profile'
      />
    </>
  );
};

export default ContactProfile;
